/**
 * Acceso Libre — Landing, Códice Botánico edition.
 * Modelo: gratuito total, patrocinado por Scram como labor social.
 *
 * Applies ui-ux-pro-max rules: SVG icons (no emoji-as-icon), codex palette
 * (no purple), visible focus via .cdx-btn, accessible contrast.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const CAPABILITIES = [
  'Plantas medicinales UNAM',
  'AlphaFold + fitoquímica',
  'Cocina inteligente',
  'Ayuno consciente',
  'Mindfulness',
  'Plan semanal estacional',
  'Huitzi · asistente IA',
  'Simulación metabólica',
  'Mapa molecular 3D',
  'Escáner NOM-051',
  'Hecho en México',
];

const Pricing = () => {
  const navigate = useNavigate();
  const handleSignup = () => navigate('/auth/register');

  return (
    <section
      className="cdx-grain relative overflow-hidden"
      style={{
        padding: '88px 0',
        background: 'linear-gradient(160deg, #E2EBDD 0%, #F4EBD9 45%, #F6E6CC 100%)',
        fontFamily: 'var(--cdx-font-body)',
      }}
    >
      <div className="cdx-greca absolute top-0 left-0 right-0 z-10" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[1]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="cdx-kicker">Labor social · Scram</span>
          <h2 className="cdx-display" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', margin: '10px 0 14px', color: 'var(--cdx-ink)' }}>
            La salud no debería <em style={{ color: 'var(--cdx-jade)', fontStyle: 'italic' }}>tener precio</em>.
          </h2>
          <p style={{ fontSize: 19, color: 'var(--cdx-ink-soft)', maxWidth: 640, margin: '0 auto', lineHeight: 1.6 }}>
            Toda la plataforma es <strong style={{ color: 'var(--cdx-jade)' }}>100% gratuita</strong> y
            seguirá siéndolo. Sin tarjeta. Sin trucos. Sin asteriscos.
          </p>
        </motion.div>

        {/* Big free folio */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="cdx-grain relative overflow-hidden mb-12"
          style={{
            background: 'var(--cdx-paper)',
            border: '1px solid var(--cdx-paper-edge)',
            borderRadius: 24,
            boxShadow: 'var(--cdx-shadow-lg)',
          }}
        >
          <div className="cdx-greca--cempa cdx-greca" />
          <div className="grid md:grid-cols-2 gap-10 items-center" style={{ padding: '36px 32px' }}>
            <div>
              <span
                className="inline-flex items-center gap-2 mb-5"
                style={{
                  background: 'linear-gradient(160deg, var(--cdx-jade-bright), var(--cdx-jade))',
                  color: 'var(--cdx-nighttext)', padding: '9px 18px', borderRadius: 999,
                  fontWeight: 700, fontSize: 13, letterSpacing: '0.04em', boxShadow: 'var(--cdx-shadow-md)',
                }}
              >
                <LeafCheck light /> GRATIS PARA SIEMPRE
              </span>
              <h3 className="cdx-display" style={{ fontSize: 28, color: 'var(--cdx-ink)', margin: '0 0 12px' }}>
                Todo incluido. Para todos.
              </h3>
              <p style={{ color: 'var(--cdx-ink-soft)', fontSize: 16.5, lineHeight: 1.6, marginBottom: 24 }}>
                Plantas medicinales, ayuno consciente, mindfulness, cocina inteligente,
                simulación metabólica y fitoquímica con AlphaFold — todo sin costo,
                para cualquier persona en México y el mundo.
              </p>
              <button onClick={handleSignup} className="cdx-btn cdx-btn--primary" style={{ padding: '15px 28px', fontSize: 17 }}>
                Crear mi cuenta gratis
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <p style={{ fontSize: 13.5, color: 'var(--cdx-ink-faint)', marginTop: 12 }}>
                Sin tarjeta de crédito · Sin límites · Sin publicidad
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {CAPABILITIES.map((label) => (
                <div
                  key={label}
                  className="flex items-center gap-2"
                  style={{
                    background: 'var(--cdx-jade-wash)',
                    border: '1px solid color-mix(in srgb, var(--cdx-jade) 22%, transparent)',
                    borderRadius: 10, padding: '9px 11px',
                  }}
                >
                  <LeafCheck />
                  <span style={{ color: 'var(--cdx-jade-deep)', fontWeight: 500, fontSize: 12.5, lineHeight: 1.25 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Why free — Scram mission (codex, no purple) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{
            background: 'var(--cdx-jade-deep)',
            borderRadius: 24,
            padding: '34px 32px',
            position: 'relative',
            overflow: 'hidden',
          }}
          className="cdx-grain"
        >
          <div className="cdx-greca--gold cdx-greca" style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
          <div className="grid md:grid-cols-3 gap-7 items-center" style={{ position: 'relative', zIndex: 1 }}>
            <div className="md:col-span-1 text-center">
              <Handshake />
              <p className="cdx-kicker" style={{ color: 'var(--cdx-gold)', marginTop: 10 }}>Patrocinado por</p>
              <p className="cdx-display" style={{ fontSize: 30, color: 'var(--cdx-nighttext)', marginTop: 2 }}>Scram</p>
            </div>
            <div className="md:col-span-2">
              <h3 className="cdx-display" style={{ fontSize: 24, color: 'var(--cdx-nighttext)', marginBottom: 12 }}>
                ¿Por qué es gratis?
              </h3>
              <p style={{ color: 'var(--cdx-night-faint)', lineHeight: 1.65, marginBottom: 12, fontSize: 15 }}>
                Porque en <strong style={{ color: 'var(--cdx-nighttext)' }}>Scram</strong> creemos que el acceso a
                información nutricional rigurosa, sabiduría ancestral mexicana y herramientas de salud
                no debería depender del ingreso de nadie. Esta plataforma es parte de nuestra{' '}
                <strong style={{ color: 'var(--cdx-gold)' }}>labor social</strong> — la fondeamos para que
                cualquier mexicana o mexicano pueda cuidar de su salud con criterio.
              </p>
              <p style={{ color: 'var(--cdx-night-faint)', lineHeight: 1.65, fontSize: 15 }}>
                Sin modelo freemium. Sin venta de datos. Sin publicidad. Solo nutrición honesta,
                respaldada por ciencia y tradición.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 items-center"
          style={{ color: 'var(--cdx-ink-faint)', fontSize: 13.5 }}
        >
          <span className="flex items-center gap-2"><Shield /> Datos encriptados · Cumple NOM-024</span>
          <span className="flex items-center gap-2"><LeafCheck /> Hecho en México con propósito</span>
          <span className="flex items-center gap-2"><BookGlyph /> Datos abiertos a la comunidad</span>
        </motion.div>
      </div>
    </section>
  );
};

/* --- SVG icons (replace emojis per ui-ux-pro-max no-emoji-icons rule) --- */
const LeafCheck = ({ light }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flexShrink: 0 }}>
    <path d="M8 14C8 9 11 5 14 4c0 5-3 9-6 10Z" fill={light ? '#EBE2CF' : '#1F5C3D'} />
    <path d="M2 8l2.4 2.6L9 5.5" stroke={light ? '#EBE2CF' : '#2E8B57'} strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.9" />
  </svg>
);
const Shield = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="var(--cdx-jade)" aria-hidden>
    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);
const BookGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cdx-copal)" strokeWidth="1.8" aria-hidden>
    <path d="M4 5a2 2 0 0 1 2-2h6v16H6a2 2 0 0 0-2 2V5Z" strokeLinejoin="round" />
    <path d="M20 5a2 2 0 0 0-2-2h-6v16h6a2 2 0 0 1 2 2V5Z" strokeLinejoin="round" />
  </svg>
);
const Handshake = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none" aria-hidden style={{ margin: '0 auto', display: 'block' }}>
    <circle cx="24" cy="24" r="22" fill="#1E2C24" stroke="#C9A227" strokeWidth="1.5" />
    <path d="M14 26l5-5 4 3 4-3 7 6-3 3-4-3-3 3-3-2-4 4z" fill="#E0892E" />
    <path d="M14 26l-2-2 4-5 3 2z" fill="#2E8B57" />
    <path d="M34 27l2-2-4-5-3 2z" fill="#2E8B57" />
  </svg>
);

export default Pricing;
